import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Lock,
  Loader2,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  KeyRound
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';

const LOGO_URL = '/logo.webp';
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    if (!token) {
      setTokenValid(false);
      setErrorMessage('No reset token provided');
      setVerifying(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/verify-reset-token?token=${token}`);
      if (response.data.valid) {
        setTokenValid(true);
        setEmail(response.data.email);
      } else {
        setTokenValid(false);
        setErrorMessage(response.data.message);
      }
    } catch (err) {
      setTokenValid(false);
      setErrorMessage('Invalid or expired reset link');
    }
    setVerifying(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successful!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
    setLoading(false);
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex" data-testid="reset-password-page">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-black to-black" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <img src={LOGO_URL} alt="Ascend & Surpass" className="h-32 w-32 mx-auto mb-8" />
            <h1 className="font-heading text-5xl text-white uppercase tracking-wider mb-4">
              Ascend & <span className="text-primary">Surpass</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-md">Create a new password and get back to your fitness journey.</p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md space-y-8"
        >
          <div className="lg:hidden flex justify-center mb-8">
            <img src={LOGO_URL} alt="Ascend & Surpass" className="h-16 w-16" />
          </div>

          {!tokenValid ? (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <div className="h-20 w-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="h-10 w-10 text-red-500" />
              </div>
              <h2 className="font-heading text-3xl text-white uppercase tracking-wider mb-4">
                Invalid <span className="text-red-500">Link</span>
              </h2>
              <p className="text-muted-foreground mb-8">{errorMessage}</p>
              <Link to="/forgot-password">
                <Button className="bg-primary text-primary-foreground font-heading tracking-wider uppercase hover:bg-primary/90">
                  Request New Link
                </Button>
              </Link>
            </motion.div>
          ) : success ? (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <div className="h-20 w-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-500" />
              </div>
              <h2 className="font-heading text-3xl text-white uppercase tracking-wider mb-4">
                Password <span className="text-primary">Reset!</span>
              </h2>
              <p className="text-muted-foreground mb-8">
                Your password has been successfully reset. You can now sign in with your new password.
              </p>
              <Link to="/login">
                <Button className="bg-primary text-primary-foreground font-heading tracking-wider uppercase hover:bg-primary/90">
                  Sign In Now
                </Button>
              </Link>
            </motion.div>
          ) : (
            <>
              <div>
                <h2 className="font-heading text-4xl text-white uppercase tracking-wider mb-2">
                  Reset <span className="text-primary">Password</span>
                </h2>
                <p className="text-muted-foreground">
                  Create a new password for <span className="text-white">{email}</span>
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-white uppercase tracking-wider text-sm">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input 
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-12 pr-12 h-14 bg-card border-border text-white focus:border-primary"
                      placeholder="••••••••"
                      required
                      minLength={6}
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  <p className="text-muted-foreground text-xs">Must be at least 6 characters</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-white uppercase tracking-wider text-sm">Confirm Password</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input 
                      id="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-12 h-14 bg-card border-border text-white focus:border-primary"
                      placeholder="••••••••"
                      required
                      data-testid="reset-confirm-password-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-14 bg-primary text-primary-foreground font-heading text-xl tracking-wider uppercase hover:bg-primary/90 transition-all hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || password.length < 6 || password !== confirmPassword}
                  data-testid="reset-password-submit"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Lock className="mr-2 h-5 w-5" />
                      Reset Password
                    </>
                  )}
                </Button>
              </form>
            </>
          )}

          <p className="text-center text-muted-foreground">
            Remember your password?{' '}
            <Link to="/login" className="text-primary hover:underline font-semibold">
              Sign In
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};
